import React, { useEffect } from 'react';
import { useController, ApplicationView, randomId } from '@aha-app/mvc';
import styles from './ConversationPanel.module.css';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import { AhaButton } from 'components/common/reactifyWebComponent';
import { SelectionScopeItem } from 'components/Assistant/components/PromptPanel/ScopeItems';

const ErrorDisplay = ApplicationView(({ error }: { error: string }) => {
  const controller = useController<AssistantViewController>();

  const fixError = () => {
    // We got an error from the app - ask the LLM to solve it for us.
    controller.actionSubmitMessage(
      `The prototype encountered this error:\n\n    ${error}\n\nPlease understand the problem and fix it`
    );
  };

  return (
    <>
      <aha-empty-state>
        <h2 slot='title'>Something went wrong</h2>
        <i
          slot='icon'
          className='far fa-face-disappointed pb-3'
          style={{ fontSize: 25, color: 'var(--aha-gray-500)' }}
        />
        <p>The prototype encountered an error:</p>
        <p>
          <pre>
            <code>{error}</code>
          </pre>
        </p>

        {controller && (
          <AhaButton
            slot='actions'
            kind='secondary'
            size='medium'
            onClick={() => fixError()}
          >
            Try to fix automatically
          </AhaButton>
        )}
      </aha-empty-state>
    </>
  );
});

export interface ReactContentProps {
  files: Record<string, string>;
  componentFileName?: string;
  contentType: string;
}

const ReactContent = ({
  files,
  contentType,
  componentFileName,
}: ReactContentProps) => {
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const controller = useController<AssistantViewController>();
  let inspectAppPrototypeEnabled = false;
  if (controller) {
    inspectAppPrototypeEnabled = controller.state.inspectAppPrototypeEnabled;
  }

  // We use a random subdomain for each app to avoid any cookies from being
  // shared between apps or with aha.io. Stored in a ref so it doesn't change
  // between renders.
  const randomSubdomain = Math.random()
    .toString(36)
    .substring(2, 10 + 2);
  const targetOrigin = React.useRef<string>(
    `https://${randomSubdomain}.${window.Configatron?.userContentDomain}`
  );

  // Listen for messages from the iframe.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== targetOrigin.current) return;
      switch (event.data.action) {
        case 'showError': {
          let error = event.data.error;
          if (error) {
            if (componentFileName) {
              error = error.replace('App.tsx', componentFileName);
            }
            setErrorMessage(error);
          }
          break;
        }
        case 'elementDeselected':
          controller.deleteScopeItem('prototype-selection');
          break;

        case 'elementSelected': {
          const { targetType, selected } = event.data;
          let source = event.data.source as string;
          if (selected) {
            if (componentFileName) {
              source = source.replace('App.tsx', componentFileName);
            }
            controller.setScopeItem('prototype-selection', {
              id: randomId() as RecordId,
              resolve: () => `targetType:${targetType}:source:${source}`,
              render: () => {
                return <SelectionScopeItem content={`${targetType} element`} />;
              },
            });
          }
          break;
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      controller.deleteScopeItem('prototype-selection');
      controller.state.inspectAppPrototypeEnabled = false;
    };
  }, [controller, componentFileName]);

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage(
        { action: 'inspect', inspectorEnabled: inspectAppPrototypeEnabled },
        targetOrigin.current
      );
    }
  }, [inspectAppPrototypeEnabled]);

  if (!window.currentAccount?.aiAllowScriptableArtifacts) {
    return (
      <aha-empty-state>
        <h2 slot='title'>Content type disabled</h2>
        Scriptable content types are disabled for your account. Please contact
        your account administrator.
      </aha-empty-state>
    );
  }

  if (errorMessage) {
    return <ErrorDisplay error={errorMessage} />;
  } else {
    return (
      <iframe
        ref={iframeRef}
        className={styles.htmlContent}
        onLoad={e => {
          e.currentTarget.removeAttribute('srcdoc');
          e.currentTarget.contentWindow?.postMessage(
            { action: 'build', files, contentType },
            targetOrigin.current
          );
        }}
        src={`${
          targetOrigin.current
        }/assistant/sandbox/react?acceptOrigin=${encodeURIComponent(
          window.location.origin
        )}`}
      />
    );
  }
};

export default ApplicationView(ReactContent);
